import { useRef } from 'react';
import { Button, CenterScreenModal } from '../atoms'
import { 
  useAddInsuranceFormModalContext,
  useErrorHandlerContext,
  useOpeningElementsControllerContext,
  usePropertyDetailContext,
  useUserLanguage,
} from '@/customHooks'
import { Col, Row } from 'react-grid-system';
import {
  useCreateInsuranceFromPropertyMutation,
  useUpdateInsuranceFromPropertyMutation,
} from '@/graphql/generated';
import {
  AddInsuranceFormControlled,
  InsuranceFormData,
  toCreateInsuranceInput,
  toUpdateInsuranceInput,
  fromInsuranceOutput,
} from '../organism/AddInsuranceFormControlled';

const textsData = {
  en: {
    title: 'Add an Insurance',
    confirmation: {
      create: {
        title: 'Insurance has been created',
        description: (name: string) => `Insurance with policy ${name} has been created succesfully`,
      },
      update: {
        title: 'Insurance has been updated',
        description: (name: string) => `Insurance with policy ${name} has been updated succesfully`,
      },
    },
    button: {
      confirm: 'Confirm',
      cancel: 'Cancel'
    }
  },
  es: {
    title: 'Agregar un Seguro',
    confirmation: {
      create: {
        title: 'Seguro creado con éxito',
        description: (name: string) => `El seguro con poliza ${name} fue creado con éxito`,
      },
      update: {
        title: 'Seguro actualizado con exito',
        description: (name: string) => `El seguro con poliza ${name} fue actualizado con éxito`,
      },
    },
    button: {
      confirm: 'Aceptar',
      cancel: 'Volver'
    }
  }
}

export const AddInsuranceFormModal = () => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const insuranceDataRef = useRef<InsuranceFormData | null>(null)
  
  const [createInsurance, { loading: createLoading }] = useCreateInsuranceFromPropertyMutation()
  const [updateInsurance, { loading: updateLoading }] = useUpdateInsuranceFromPropertyMutation()

  const { confirmDialogModalOpen } = useOpeningElementsControllerContext()

  const { insuranceData, isOpen, closeModal, } = useAddInsuranceFormModalContext()

  const { property, setProperty } = usePropertyDetailContext()
  
  const { open } = useErrorHandlerContext()

  const OnChangeHandle = (data: InsuranceFormData) => {
    insuranceDataRef.current = data
  }

  const onConfirmHandle = () => {
    if (!insuranceDataRef.current) return
    
    if (insuranceData === null) {
      const input = toCreateInsuranceInput(insuranceDataRef.current)
      createInsurance({ variables: {
        propertyId: property?.id ?? '',
        insuranceInput: input,
      }})
      .then((response) => {
        setProperty(undefined)
        closeModal()
        confirmDialogModalOpen({
          title: texts.confirmation.create.title,
          description: texts.confirmation.create.description(response.data?.createInsuranceFromProperty.policyNumber ?? ''),
        })
      })
      .catch((error) => {
        closeModal()
        open(error)
      })
    } 
    else {
      const input = toUpdateInsuranceInput(insuranceDataRef.current)
      updateInsurance({ variables: {
        propertyId: property?.id ?? '',
        insuranceId: insuranceData.id,
        insuranceInput: input,
      }})
      .then((response) => {
        setProperty(undefined)
        closeModal()
        confirmDialogModalOpen({
          title: texts.confirmation.update.title,
          description: texts.confirmation.update.description(response.data?.updateInsuranceFromProperty.policyNumber ?? '')
        })
      })
      .catch((error) => {
        closeModal()
        open(error)
      })  
    }
  }
  
  return (
    <CenterScreenModal
      isOpen={isOpen}
      onClose={closeModal}
    >
      <Row>
        <Col>
          <h2>{texts.title}</h2>
        </Col>
      </Row>
      <AddInsuranceFormControlled
        defaultData={insuranceData ? fromInsuranceOutput(insuranceData) : undefined}
        onChange={OnChangeHandle}
      />
      <br />
      <Row justify="center" gutterWidth={32}>
        <Col xs="content">
          <Button secondary onClick={() => closeModal()} disabled={!!updateLoading || !!createLoading}>
            {texts.button.cancel}
          </Button>
        </Col>
        <Col xs="content">
          <Button
            onClick={() => onConfirmHandle()}
            isLoading={!!updateLoading || !!createLoading}
          >
            {texts.button.confirm}
          </Button>
        </Col>
      </Row>
    </CenterScreenModal>
  )
}
